import { format } from 'date-fns/format';
import {
  compressSessionStorage,
  decompressSessionStorage,
} from './sessionStorage';

// Sentry
import * as Sentry from '@sentry/react';
import i18n from '../i18n';

export function fileToBase64(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result as string;
      resolve(base64String);
    };
    reader.onerror = (error) => {
      reject(error);
    };
    reader.readAsDataURL(file);
  });
}

export function getFilesFromSessionStorage(
  sessionStorageKey: string
): File[] | null {
  const filesDataJSON = sessionStorage.getItem(sessionStorageKey);
  if (filesDataJSON) {
    const filesData = JSON.parse(decompressSessionStorage(filesDataJSON)) as {
      name: string;
      content: string;
    }[];
    const files: File[] = filesData.map((fileData) => {
      const binaryString = atob(fileData.content.split(',')[1]);
      const byteArray = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        byteArray[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([byteArray], {
        type: 'application/octet-stream',
      });
      return new File([blob], fileData.name);
    });
    return files;
  } else {
    return null;
  }
}

export async function saveFilesToSessionStorage(
  files: File[],
  sessionStorageKey: string
) {
  const filesToSave = files.map(async (file) => {
    const base64String = await fileToBase64(file);
    return {
      name: file.name,
      content: base64String,
    };
  });

  const filesData = [];
  for (const res of await Promise.all(filesToSave)) {
    filesData.push(res);
  }

  sessionStorage.setItem(
    sessionStorageKey,
    compressSessionStorage(JSON.stringify(filesData))
  );
}

export async function getSpecificFileFromDirectory(
  directoryName: string,
  fileName: string
): Promise<{ file: File; name: string } | null> {
  try {
    const opfsRoot = await navigator.storage.getDirectory();
    const picturesFolder = await opfsRoot.getDirectoryHandle(directoryName, {
      create: false,
    });

    const fileSystem = await picturesFolder.getFileHandle(fileName);
    const myFile = await fileSystem.getFile();
    return { file: myFile, name: myFile.name };
  } catch (err) {
    // «No existe» es una respuesta, no un error: `saveFilesIntoADirectory`
    // pregunta antes de escribir CADA fichero nuevo, y esto mandaba un evento a
    // Sentry por foto (ROIS-FRONT-25/6K/6G/1C). Una pérdida real la reporta el
    // sync manager con su orden (spec 2026-10-01-photo-outbox-identity § 5.5).
    if ((err as { name?: string })?.name !== 'NotFoundError') {
      Sentry.captureException(err);
      console.log('[archivos] error en operación de fichero', err);
    }
    return null;
  }
}

/**
 * ¿Está el fichero? Distingue «no existe» (`NotFoundError`, también si falta el
 * directorio) de «no se pudo mirar» (cualquier otro error de OPFS).
 *
 * La bandeja de fotos solo da por perdida una imagen en el primer caso: un
 * error pasajero al leer no puede sacar de la cola una entrada cuyo fichero
 * sigue ahí (spec 2026-10-01-photo-outbox-identity § 5.4).
 */
export async function checkFileInDirectory(
  directoryName: string,
  fileName: string
): Promise<'present' | 'missing' | 'unknown'> {
  try {
    const opfsRoot = await navigator.storage.getDirectory();
    const folder = await opfsRoot.getDirectoryHandle(directoryName, {
      create: false,
    });
    await folder.getFileHandle(fileName);
    return 'present';
  } catch (err) {
    return (err as { name?: string })?.name === 'NotFoundError'
      ? 'missing'
      : 'unknown';
  }
}

export async function saveFilesIntoADirectory(
  directoryName: string,
  filesToSave: File[]
) {
  const opfsRoot = await navigator.storage.getDirectory();
  const picturesFolder = await opfsRoot.getDirectoryHandle(directoryName, {
    create: true,
  });

  const storeFile = async (file: File, folder: FileSystemDirectoryHandle) => {
    const reader = new FileReader();
    return new Promise<void>((resolve, reject) => {
      reader.onload = async () => {
        const fileName = file.name;
        const fileHandle = (await folder.getFileHandle(fileName, {
          create: true,
        })) as any;
        const writable = await fileHandle.createWritable();
        await writable.write(reader.result);
        await writable.close();
        resolve();
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsArrayBuffer(file);
    });
  };

  await Promise.all(
    filesToSave.map(async (file) => {
      try {
        !(await getSpecificFileFromDirectory(directoryName, file.name)) &&
          (await storeFile(file, picturesFolder));
      } catch (err) {
        Sentry.captureException(err);
        console.log('[archivos] error en operación de fichero', err);
      }
    })
  );
}

export async function getFilesFromDirectory(
  directoryName: string
): Promise<{ file: File; id: string }[]> {
  const opfsRoot = await navigator.storage.getDirectory();
  const picturesFolder = await opfsRoot.getDirectoryHandle(directoryName, {
    create: false,
  });

  const filesSaved = [];

  for await (const [, file] of picturesFolder as unknown as any[]) {
    if (file.kind !== 'directory') {
      let fileToAssign: File;
      try {
        fileToAssign = (await file.getFile()) as File;
      } catch (err) {
        // 🔴 La carpeta la comparten las pestañas y los asistentes (cada
        // borrador tiene su fichero, Ruling 15): otra pestaña puede borrar el
        // suyo mientras se recorre. Ese fichero ya no existe y no es de este
        // borrador — se salta. Antes el `NotFoundError` tumbaba la subida y el
        // guardado entero (Ruling 20). Cualquier otro fallo sigue lanzando.
        if ((err as { name?: string })?.name === 'NotFoundError') continue;
        throw err;
      }

      filesSaved.push({ file: fileToAssign, id: fileToAssign.name });
    }
  }

  return filesSaved;
}

export async function deleteFilesFromDirectory(
  directoryName: string,
  fileName: string
) {
  const opfsRoot = await navigator.storage.getDirectory();
  const picturesFolder = await opfsRoot.getDirectoryHandle(directoryName, {
    create: true,
  });

  try {
    await picturesFolder.removeEntry(fileName, { recursive: true });
  } catch (err) {
    // Borrar un fichero que NO está en OPFS (p.ej. una imagen del servidor que
    // nunca se subió en esta sesión) no es un error: el borrado real lo hace el
    // save (DELETE_MANY_IMAGES). Idempotente → ignoramos solo NotFoundError.
    // Antes: rechazo "Uncaught (in promise) NotFoundError" al quitar una imagen
    // existente, aunque el borrado funcionaba igual.
    if ((err as { name?: string })?.name !== 'NotFoundError') {
      throw err;
    }
  }
}

/**
 * Borra TODAS las entradas de la raíz de OPFS. Es para el cierre de sesión.
 *
 * Todo lo que la app guarda en OPFS es trabajo del usuario: adjuntos y croquis
 * de los asistentes (`action`, `accident`, `canvas-*`), las fotos de la bandeja
 * pendientes de subir (`offline-pending-uploads`) y los borradores de fotos de
 * tramo (`segment-photos-<id>`). Antes el logout borraba una lista fija de 4
 * carpetas y las otras sobrevivían: en una tablet compartida, el siguiente
 * operario heredaba las fotos del anterior. Barrer la raíz cubre también las
 * carpetas que se añadan en el futuro.
 *
 * Un fallo en una entrada (p. ej. `NoModificationAllowedError` con un fichero
 * abierto) se registra y no impide borrar las demás.
 *
 * `keep`: carpetas de la raíz que se conservan. El logout conserva
 * `offline-pending-uploads`: son las fotos que esperan a subir, trabajo sin
 * enviar con dueño (spec 2026-10-12).
 */
export async function deleteAllOpfsEntries(
  { keep = [] }: { keep?: readonly string[] } = {},
): Promise<void> {
  if (typeof navigator === 'undefined' || !navigator.storage?.getDirectory) {
    return;
  }
  const opfsRoot = await navigator.storage.getDirectory();
  const names: string[] = [];
  for await (const [name] of opfsRoot as unknown as AsyncIterable<[string, unknown]>) {
    names.push(name);
  }
  for (const name of names) {
    if (keep.includes(name)) continue;
    try {
      await opfsRoot.removeEntry(name, { recursive: true });
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: 'logout', step: 'opfs' } });
    }
  }
}

/**
 * Los ficheros de una carpeta de OPFS con la fecha de su última escritura.
 * Carpeta inexistente = `[]`; cualquier otro fallo se lanza, para que quien
 * decide borrar no confunda «no pude mirar» con «no hay nada».
 */
export async function listFilesWithDates(
  directoryName: string,
): Promise<{ name: string; lastModified: number }[]> {
  const opfsRoot = await navigator.storage.getDirectory();
  let folder: FileSystemDirectoryHandle;
  try {
    folder = await opfsRoot.getDirectoryHandle(directoryName, { create: false });
  } catch (err) {
    if ((err as { name?: string })?.name === 'NotFoundError') return [];
    throw err;
  }
  const files: { name: string; lastModified: number }[] = [];
  for await (const [name, handle] of folder as unknown as AsyncIterable<
    [string, FileSystemHandle]
  >) {
    if (handle.kind !== 'file') continue;
    const file = await (handle as FileSystemFileHandle).getFile();
    files.push({ name, lastModified: file.lastModified });
  }
  return files;
}

export async function deleteWholeDirectory(directoryName: string) {
  const opfsRoot = await navigator.storage.getDirectory();
  const picturesFolder = await opfsRoot.getDirectoryHandle(directoryName, {
    create: true,
  });

  for await (const [, file] of picturesFolder as unknown as any[]) {
    if (file.kind !== 'directory') {
      const fileToAssign = (await file.getFile()) as File;

      await picturesFolder.removeEntry(fileToAssign.name, { recursive: true });
    }
  }
}

export const generateFileName = (
  type: string,
  dataBaseNamingCaching: string
) => {
  let road = '';
  const roadRaw = sessionStorage.getItem(dataBaseNamingCaching);

  if (roadRaw) {
    road = JSON.parse(
      decompressSessionStorage(
        sessionStorage.getItem(dataBaseNamingCaching) as string
      )
    ).road;
  }

  const date = format(new Date(), 'dd_MM_yy-HH_mm_ss');

  return `${type}_${road ? road + '_' : road}${date}`;
};

export const dataURItoBlob = (dataURI: string) => {
  const byteString = atob(dataURI.split(',')[1]);
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);

  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new Blob([ab], { type: mimeString });
};
